import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import type {
  WorkerTemplate,
  DeployWorkerPayload,
} from "@/services/api/worker-ani/types";
import { WorkerAniAPI } from "@/services/api";

export interface WorkerCreationData {
  // Step 1: Worker Setup
  name: string;
  _id: string;
  jobTitle: string;
  bio: string;
  rules: string;
  instructions: string;

  // Step 2: Capabilities
  selectedCapabilities: Set<string>;

  // Step 3: Sharing & Permissions
  editing_permissions?: {
    type: "owner_only" | "company_wide" | "specific_users";
    shared_editors?: string[];
  };

  // Step 4: Test (no additional data needed)
}

export interface WorkerCreationContextType {
  workerData: WorkerCreationData;
  updateWorkerData: (updates: Partial<WorkerCreationData>) => void;
  saveWorkerToAPI: () => Promise<void>;
  resetWorkerData: () => void;
  clearStaleData: () => void;
  selectedTemplate: WorkerTemplate | null;
  setSelectedTemplate: (
    template: WorkerTemplate | null,
    options?: { preserveData?: boolean }
  ) => void;
}

const defaultWorkerData: WorkerCreationData = {
  name: "Worker",
  _id: "",
  jobTitle: "",
  bio: "",
  rules: "",
  instructions: "",
  selectedCapabilities: new Set([]),
};

// Storage keys
const STORAGE_KEYS = {
  WORKER_DATA: "worker-creation-data",
  SELECTED_TEMPLATE: "worker-creation-template",
};

// Helper functions for localStorage with Set serialization
const saveToStorage = (key: string, data: any) => {
  try {
    const serialized = JSON.stringify(data, (key, value) => {
      if (value instanceof Set) {
        return Array.from(value);
      }
      return value;
    });
    localStorage.setItem(key, serialized);
  } catch (error) {
    console.warn("Failed to save to localStorage:", error);
  }
};

const loadFromStorage = (key: string, defaultValue: any) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return defaultValue;

    const parsed = JSON.parse(stored);

    // Handle Set reconstruction for worker data
    if (key === STORAGE_KEYS.WORKER_DATA && parsed.selectedCapabilities) {
      parsed.selectedCapabilities = new Set(parsed.selectedCapabilities);
    }

    return parsed;
  } catch (error) {
    console.warn("Failed to load from localStorage:", error);
    return defaultValue;
  }
};

const WorkerCreationContext = createContext<
  WorkerCreationContextType | undefined
>(undefined);

export function WorkerCreationProvider({ children }: { children: ReactNode }) {
  // Initialize state from localStorage
  const [workerData, setWorkerData] = useState<WorkerCreationData>(() =>
    loadFromStorage(STORAGE_KEYS.WORKER_DATA, defaultWorkerData)
  );
  const [selectedTemplate, setSelectedTemplateState] =
    useState<WorkerTemplate | null>(() =>
      loadFromStorage(STORAGE_KEYS.SELECTED_TEMPLATE, null)
    );

  // Save to localStorage whenever data changes
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.WORKER_DATA, workerData);
  }, [workerData]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.SELECTED_TEMPLATE, selectedTemplate);
  }, [selectedTemplate]);

  const saveWorkerToAPI = useCallback(async () => {
    // Only save to API if we have a worker ID (draft exists)
    if (!workerData._id) {
      return;
    }

    try {
      const payload: DeployWorkerPayload = {
        template_id: selectedTemplate?._id ?? null,
        name: workerData.name,
        job_title: workerData.jobTitle,
        bio: workerData.bio,
        instructions: workerData.instructions,
        rules: (workerData.rules || "").split("\n").filter(Boolean),
        capabilities: Array.from(workerData.selectedCapabilities),
        editing_permissions: workerData.editing_permissions,
      };

      await WorkerAniAPI.updateWorker(workerData._id, payload);
    } catch (error) {
      console.error("Failed to save worker data to API:", error);
      // Don't throw error to avoid breaking the UI flow
    }
  }, [workerData, selectedTemplate]);

  const updateWorkerData = useCallback(
    (updates: Partial<WorkerCreationData>) => {
      setWorkerData((prev) => {
        const newData = { ...prev, ...updates };
        // Handle Set updates properly
        if (updates.selectedCapabilities) {
          newData.selectedCapabilities = new Set(updates.selectedCapabilities);
        }
        return newData;
      });
    },
    []
  );

  const resetWorkerData = useCallback(() => {
    setWorkerData(defaultWorkerData);
    setSelectedTemplateState(null);
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEYS.WORKER_DATA);
    localStorage.removeItem(STORAGE_KEYS.SELECTED_TEMPLATE);
  }, []);

  const clearStaleData = useCallback(() => {
    // Only clear if we don't have a current edit session
    // This helps when users navigate away and come back
    const hasEditSession = workerData._id && workerData._id !== "";
    if (!hasEditSession) {
      resetWorkerData();
    }
  }, [workerData._id, resetWorkerData]);

  const setSelectedTemplate = useCallback(
    (template: WorkerTemplate | null, options?: { preserveData?: boolean }) => {
      // When preserving data, just set the template and exit.
      if (options?.preserveData) {
        setSelectedTemplateState(template);
        return;
      }

      // Always reset worker data when selecting a template to avoid stale data
      if (template) {
        const templateDefaults = {
          name: template.name || "Worker",
          _id: "",
          jobTitle: template.default_job_title || "",
          bio: template.default_bio || "",
          rules: template.default_rules?.join("\n") || "",
          instructions:
            (template as any).default_instructions?.join("\n") || "",
          selectedCapabilities: new Set(template.default_capabilities || []),
        };
        setWorkerData(templateDefaults);
      } else {
        // If no template selected, reset to defaults
        setWorkerData(defaultWorkerData);
      }

      setSelectedTemplateState(template);
    },
    []
  );

  return (
    <WorkerCreationContext.Provider
      value={{
        workerData,
        updateWorkerData,
        saveWorkerToAPI,
        resetWorkerData,
        clearStaleData,
        selectedTemplate,
        setSelectedTemplate,
      }}
    >
      {children}
    </WorkerCreationContext.Provider>
  );
}

export function useWorkerCreation() {
  const context = useContext(WorkerCreationContext);
  if (context === undefined) {
    throw new Error(
      "useWorkerCreation must be used within a WorkerCreationProvider"
    );
  }
  return context;
}
